/** @module csi/table/ToolbarPluginChart */
define([
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/promise/all",
	"dojo/store/Memory",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/core/Key",
	"csi/constants",
	"csi/def",
	"csi/def/Action",
	"csi/html",
	"csi/locale",
	"csi/prefs",
	"csi/util",
	"csi/view/PluginFactory",
	"csi/view/ToolbarPluginButton",
	"csi/widget/FusionChart",
	"csi/xml"
], function(lang, aspect, domClass, domConstruct, all, Memory, Deferred, _, Key, constants, def, Action, html, locale, csiPrefs, util, PluginFactory, ToolbarPluginButton, FusionChart, xml) {

	var CHART_HEIGHTS = {};
	CHART_HEIGHTS[constants.CHART_HEIGHT_SMALL] = 200;
	CHART_HEIGHTS[constants.CHART_HEIGHT_MEDIUM] = 300;
	CHART_HEIGHTS[constants.CHART_HEIGHT_LARGE] = 400;

	function _getChartWidth(/*Number*/availableWidth, /*Number*/nCharts) {
		// summary: determine width of charts to best fit the available space
		var borderWidth = 2;
		var minWidth = 300 + borderWidth;

		if (nCharts == 1) {
			return Math.min(availableWidth - borderWidth, 1000);
		}

		var marginWidth = 10;
		var nChartsPerRow = Math.min(Math.floor(availableWidth / minWidth), nCharts);
		var remainder = availableWidth - (nChartsPerRow * minWidth);

		// if not room left for margins, then decrease number of charts per row by 1
		if (remainder < marginWidth * nChartsPerRow) {
			--nChartsPerRow;
		}

		// subtract space needed for margins from available width
		availableWidth -= marginWidth * nChartsPerRow;

		// finally, calculate chart width based on available space, making sure to subtract width of borders included in the calculations above
		return Math.floor(availableWidth / nChartsPerRow) - borderWidth;
	}

	function _unslick(jChartNode) {
		if (jChartNode.hasClass("slick-slider")) {
			jChartNode.slick("unslick");
		}
	}

	return PluginFactory.declare("charts", ToolbarPluginButton, {
		// DOMNode: form for chart settings
		chartForm: null,

		// Boolean: handle special case where name of items in chart on parent rather than given node
		//bUseParentForName: false,

		// Memory: store containing attributes to group by
		groupStore: null,

		/** @property {boolean} hasRendered - indicates charts are already rendered, so no need to re-render */
		//hasRendered: false,

		// Memory: store containing numeric attributes to group
		valueStore: null,

		// Boolean: indicates table in process of applying chart filter, in which case we do not want to re-render charts
		//applyingFilter: false,

		// RegExp: regular expression for attributes to group by
		_reGroup: /(boolean|enum|ref)$/,

		/** @property {Object} _resizeHandle - handle to resize listener */
		//_resizeHandle: null,

		/**
		 * @override
		 * Only show the plugin if user can edit charts
		 */
		canShowPlugin: function() {
			return csiPrefs.hasPersonalAccess();
		},

		validatePlugin: function(/*Context*/ctx) {
			// summary: charting not allowed if no preferences or if toolbar does not appear within this view
			return !!ctx.view.chartNode &&
				!!ctx.view.supportsCustomViews() &&
				ctx.view.relRoot &&
				ctx.defView.getPageClass();
		},

		enablePlugin: function(/*Context?*/ctx) {
			// summary: determine if possible to create chart based on available stores
			// also, the chart data must be present in the custom view
			var bDisabled = !ctx || ctx.bPrint || !this.hasSelectOption() || !csiPrefs.hasPersonalAccess();

			// disable and update tooltip if results exceed limit
			var limit = ctx && ctx.view.getQueryLimitIfReached();
			if (limit) {
				this.set("title", locale.resource("TableNewChartMax_TT", [limit]));
				bDisabled = true;
			} else {
				this.set("title", this.defAction.m_tooltip);
			}

			this._setDisabled(this, bDisabled);
		},

		postMixInProperties: function() {
			// summary: populate actions and set widget appearance based on action
			var defView = this.getView().defView;

			defView._addViewAction("TableFilterByChart", {classController: "csi/view/QueryController"});
			defView._addViewAction("TableEditChart", {classController: "csi/table/EditChartController"});
			defView._addViewAction("TableRemoveChart", {classController: "csi/table/ChartController"});

			this.defAction = lang.mixin(new Action(), {
				m_id: "TableNewChart",
				m_class: "iconChart",
				m_typeControl: def.ACTION_ICON,
				classController: "csi/table/NewChartController"
			});
			this.defAction.resolve();

			this.inherited(arguments);
		},

		startup: function() {
			// summary: create text drop down dialog
			var view = this.actionToolbar.view;

			this.own(
				aspect.after(view, "onBuildColumns", (ctx, dfds) => this._onRenderCharts(ctx, dfds, true), true),
				aspect.after(view, "_postViewDeferred", (ctx, dfds) => this._onRenderCharts(ctx, dfds, false), true)
			);

			this.inherited(arguments);
		},

		editFirstChart: function() {
			// summary: edit first (and only in this case) chart
			var view = this.getView();
			var chartData = view.get("chartSettings");
			if (chartData && chartData[0]) {
				this._invokeAction(view.getAction("TableEditChart"), null, {
					actionValue: chartData[0],
					chartIndex: 0
				});
			}
		},

		getGroupStore: function() {
			// summary: return store of headings that can be used for grouping
			return this.groupStore;
		},

		getValueStore: function() {
			// summary: return store of headings that can be used for grouping
			return this.valueStore;
		},

		getTypeCharted: function() {
			// summary: return default name used for chart titles/axes
			var rc = this._getClassForName();
			return locale.typePlural(rc.getType());
		},

		hasSelectOption: function() {
			// summary: return true if view has attribute than can be charted
			return !!this.groupStore || !!this.valueStore;
		},

		/**
		 * Resize charts to fit available space (down to threshold), and apply slick plugin if the charts exceed available space
		 * @param {boolean} bInit - true if first time fitting charts
		 */
		resizeCharts: function(bInit) {
			var chartNode = this.getView().chartNode;
			var jChartNode = $(chartNode);
			var jCharts = jChartNode.find(".csi-fusion-chart");
			var nCharts = jCharts.length;

			if (bInit) {
				// the slick plugin now applies width styling to the individual charts (width:100%), so need to wrap
				// all charts with another div to stop that from overwriting the width we are applying
				jCharts.wrap("<div></div>");
			} else {
				jCharts.css("width", "");
				_unslick(jChartNode);
			}

			// apply fixed width to all charts
			var availableWidth = chartNode.offsetWidth;
			var chartWidth = _getChartWidth(availableWidth, nCharts);
			jCharts.width(chartWidth);

			// apply slick plugin to enable scrolling of charts if not enough space to fit charts
			var totalWidth = chartNode.scrollWidth;

			if ((nCharts >= 2) && (availableWidth < totalWidth)) {
				var nSlides = Math.floor(availableWidth / chartWidth);

				jChartNode.slick({
					dots: true,
					infinite: false,
					slidesToShow: nSlides,
					slidesToScroll: nSlides
				});
			}
		},

		_getChartIndex: function(/*FusionChart*/chart) {
			// summary: return index of chart among all charts
			return _.findIndex(this.getView().chartSettings, function(obj) {
				return _.isEqual(obj, chart.settings);
			});
		},

		_getClassForName: function() {
			// summary: return the reflection class to be used for axis names
			var view = this.actionToolbar.view;
			return view.defView.getPageClass();
		},

		_getHeadingStore: function(/*RegExp*/re) {
			// summary: return store with headings whose type matches regular expression
			const specs = this.getView().getQueryLevelSpecs();
			const data = [];

			specs.traverse(false, (spec) => {
				const {key, name, type} = spec;
				if (type && type.match(re)) {
					data.push({
						id: key.typedString,
						label: spec.getExtendedName(),
						name,
					});
				}
			});

			if (data) {
				data.unshift({id: "", label: "", name: ""});

				return new Memory({
					labelProperty: "label",
					data,
				});
			}

			return null;
		},

		_onClickChart: function(/*FusionChart*/chart, /*Any*/value1, /*Any?*/value2) {
			// summary: filter table by given values
			var self = this;
			var settings = chart.get("settings");
			var view = this.actionToolbar.view;

			var applyFilter = function(typedKey, value) {
				var spec = view.columnData.getByKey(typedKey, true);
				if (spec) {
					if (spec.type == xml.ATTR_BOOLEAN) {
						value = xml.valueIsStringTrue(value);
					}

					view.setQueryValue(spec.queryKey, [value]);
				}
			};

			if (settings.chartGroupX) {
				applyFilter(settings.chartGroupX, value1 || xml.VALUE_BLANK);

				if (settings.chartGroupY) {
					applyFilter(settings.chartGroupY, value2 || xml.VALUE_BLANK);
				}
			} else {
				const extendedPath = view.specBuilder.defaultSourcePath;
				const key = new Key({extendedPath, id: xml.ATTR_NAME}).typedString;
				applyFilter(key, value1);

			}

			this.applyingFilter = true;

			this._invokeAction(view.getAction("TableFilterByChart"), null, {
				onEnd: function() {
					self.applyingFilter = false;
				}
			});
		},

		/**
		 * Signal to render charts - disregard if in the middle of applying filter by clicking on the chart
		 * @param {Context} ctx
		 * @param {Deferred[]} dfds
		 * @param {boolean} renderAtStart - if true, then charts are being rendered at start of building view rather than at end
		 */
		_onRenderCharts: function(ctx, dfds, renderAtStart) {
			const {applyingFilter, hasRendered} = this;

			if (!applyingFilter) {
				// update heading stores when table header is rebuilt
				if (renderAtStart) {
					this.groupStore = this._getHeadingStore(this._reGroup);
					this.valueStore = this._getHeadingStore(xml.RE_NUMERIC);
				}

				// the plugin is listening to functions both before and after rendering the table; if the full
				// view is being refreshed, then the charts will be rendered first, and the second call will
				// be disregarded; if the table body is just being redrawn, then charts will be rendered last.
				// This is done so that the automatic height of the table is determined correctly.
				this.hasRendered = renderAtStart;

				if (!hasRendered) {
					// animate so long as not printing
					dfds.push(this.renderCharts(!ctx.bPrint));
				}
			}
		},

		_onRemoveChart: function(/*FusionChart*/chart) {
			// summary: remove chart from custom view
			this._invokeAction(this.getView().getAction("TableRemoveChart"), null, {
				actionValue: null,
				chartIndex: this._getChartIndex(chart),
				onPostProcessPreferences: function() {
					var chartNode = this.widget.chartNode;
					var plugin = this.actionWidget;

					_unslick($(chartNode));

					html.destroyElement(chartNode.childNodes[this.chartIndex]);

					if (chartNode.firstChild) {
						plugin.resizeCharts();
					} else {
						plugin.renderCharts();
					}
				}
			});
		},

		_onEditChart: function(/*FusionChart*/chart) {
			// summary: edit the selected chart from custom view
			this._invokeAction(this.getView().getAction("TableEditChart"), null, {
				actionValue: chart.get("settings"),
				chartIndex: this._getChartIndex(chart)
			});
		},

		_renderChart: function(/*Object*/chartData, /*FusionChartSettings*/settings, /*FusionChart?*/activeChart, /*Boolean*/bAnimate) {
			// summary: process results into chart JSON given chart settings
			const {
				chartXAxisName,
				chartYAxisName,
				chartGroupX,
				chartValue,
				chartValue2,
			} = settings;

			const {
				data,
				xValuesAreEnums,
				yValuesAreEnums,
			} = chartData;
			let {yValues} = chartData;

			var view = this.getView();
			var ctx = view.getContext();
			var typeName = this.getTypeCharted();
			var hX = view.getHeading(chartGroupX);
			var hV = view.getHeading(chartValue);
			var hV2 = view.getHeading(chartValue2);
			var params = {};
			var xLabel;
			var yLabel;

			if (hX) {
				xLabel = chartXAxisName === "" ? hX.m_name : chartXAxisName;
				yLabel = chartYAxisName === "" ? (hV ? hV.m_name : typeName) : chartYAxisName;
			} else if (hV) {
				xLabel = chartXAxisName === "" ? typeName : chartXAxisName;
				yLabel = chartYAxisName === "" ? hV.m_name : chartYAxisName;

				if (hV2) {
					// data array is pair of node value lists if a second value is introduced
					yLabel = "";

					if (!yValues) {
						// introduce the pair of data point names for each series
						yValues = [
							[hV.m_name, hV.m_name],
							[hV2.m_name, hV2.m_name]
						];
					}
				}
			}

			let xValues = chartData.xValues || [];
			// hide labels if too many results
			if (xValues.length > 10) {
				params.showValues = 0;
				params.showPercentageValues = 0;
			}

			if (xValues.length > 20) {
				params.showLabels = 0;
			}

			// localize enumerations if necessary
			if (xValuesAreEnums) {
				_.each(xValues, function(a) {
					a[1] = locale.getEnumName(a[1]);
				});
			}

			if (yValuesAreEnums) {
				_.each(yValues, function(a) {
					a[1] = locale.getEnumName(a[1]);
				});
			}

			// create chart with data and insert into table view
			var bFilter = view.canFilter();
			var format = hV ? hV.m_format : null;

			if (hV2 && (hV2.m_format != format)) {
				format = null;
			}

			var chart = new FusionChart({
				bAnimate,
				bForPrint: ctx.bPrint,
				bLink: bFilter,
				data,
				format,
				params,
				settings,
				xLabel,
				yLabel,
				xValues,
				yValues
			});

			if (csiPrefs.hasPersonalAccess()) {
				chart.own(
					aspect.after(chart, "onEdit", lang.hitch(this, this._onEditChart, chart), true),
					aspect.after(chart, "onRemove", lang.hitch(this, this._onRemoveChart, chart), true)
				);
			} else {
				chart.removeEditingWidgets();
			}

			if (bFilter) {
				chart.own(aspect.after(chart, "onClick", lang.hitch(this, this._onClickChart, chart), true));
			}

			if (activeChart) {
				chart.placeAt(activeChart, "after");
				activeChart.destroy();
			} else {
				chart.placeAt(view.chartNode);
			}

			return chart.deferred;
		},

		renderCharts: function(/*Boolean*/bAnimate) {
			// summary: render charts in view, returning Deferred that will be resolved when complete
			var dfd = new Deferred();
			var view = this.getView();
			var chartData = view.get("chartSettings");
			var nCharts = chartData && chartData.length;
			var chartNode = view.chartNode;
			var chartWidth;
			var ctx = view.getContext();
			var store = view.get("store");

			_unslick($(chartNode));

			if (this._resizeHandle) {
				this._resizeHandle.remove();
				this._resizeHandle = null;
			}

			// need to delete all old chartNodes to prevent blank chart spaces from repeating while creating slick chart
			domConstruct.empty(chartNode);
			domClass.remove(chartNode, "tableViewChartsWrap");

			if (!nCharts || !store || view.get("suspended") || view.get("chartHide") || view.getQueryLimitIfReached()) {
				// ensure that table is shown when charts are not
				if (!view.errorOnLoad) {
					html.setVisible(view.scrollNode, true);
				}

				dfd.resolve();
				return dfd;
			}

			// hide tables if so configured when charts are present
			view.updateChartTableVisibility();

			// if printing, then allow charts to wrap and determine exact width
			if (ctx.bPrint) {
				chartWidth = _getChartWidth(chartNode.offsetWidth, nCharts);
			}

			// prepare document with chart settings to send to server
			var chartParamsDoc = util.newXMLDocument();
			var charts = chartParamsDoc.createElement("charts");
			var headings = [];

			function addElementWithText(/*XMLNode*/parentNode, /*String*/elementName, /*String*/text) {
				var elem = chartParamsDoc.createElement(elementName);
				var textNode = chartParamsDoc.createTextNode(text);
				elem.appendChild(textNode);
				parentNode.appendChild(elem);
			}

			function addHeading(/*XMLNode*/chart, /*String*/paramName, /*String*/dpkey) {
				const spec = dpkey ? view.getHeadingSpec(dpkey) : null;
				if (spec) {
					let {key} = spec;

					// the name will mask the attribute id, so remove the name to generate the key using the id
					if (key.name) {
						key = key.copy({name: null});
					}

					addElementWithText(chart, paramName, key.toString());
					headings.push(spec.heading);
				}
			}

			chartParamsDoc.appendChild(charts);

			_.each(chartData, function(obj) {
				var chart = chartParamsDoc.createElement("chart");
				addHeading(chart, "value", obj.chartValue);
				addHeading(chart, "value2", obj.chartValue2);
				addHeading(chart, "groupX", obj.chartGroupX);
				addHeading(chart, "groupY", obj.chartGroupY);
				obj.chartHeight = CHART_HEIGHTS[view.chartHeight];
				obj.chartWidth = chartWidth;

				if (this.bUseParentForName) {
					addElementWithText(chart, "useParentForName", true);
				}

				charts.appendChild(chart);
			}, this);

			var chartStore = store.getChartStore({
				requestParameters: {
					"ChartParams": util.serializeXML(chartParamsDoc),
					"Dep.Path": def.getHeadingsDependencyPaths(headings)
				}
			});

			var self = this;

			view.when(chartStore.query(),
				function(results) {
					// If chart is configured to be sorted - sort the data
					// Sort currently only enabled for not multi-series/not stacked charts. Which
					// means, that yValues and 2 dimensional data arrays are not the case for sorting
					_.each(results, function(res, i) {
						var bSorted = chartData[i].chartSortBy && (chartData[i].chartSortBy != "default");
						if (bSorted) {
							var bAsc = chartData[i].chartSortBy == "valAsc";
							var sortObj = [];

							_.each(res.data, function(d, j) { // Create sort object
								sortObj[j] = {data: res.data[j], xValue: res.xValues[j]};
							});
							sortObj = _.sortBy(sortObj, function(item) { // Do Sort
								return bAsc ? Number(item.data) : -item.data; // Operation "-" converts to Number
							});
							_.each(sortObj, function(item, j) { // Apply sorted data to results
								res.data[j] = item.data;
								res.xValues[j] = item.xValue;
							});
						}
					});

					var dfds = results.map(function(item, i) {
						return self._renderChart(item, chartData[i], null, bAnimate);
					});

					// There might be no visible charts even if nCharts > 0. Its the case when we have no data for charts, then _renderChart() returns null
					dfds = _.compact(dfds);  // removes falsy values

					// Check if at least one graph has data (_renderChart() returned not null)
					if (dfds.length) {
						view.when(all(dfds), function() {
							if (!ctx.bPrint) {
								if (!view.chartHideTable && !view.bPrintView) {
									// fit charts in the view
									self.resizeCharts(true);

									// listen for resize events
									self._resizeHandle = aspect.before(view, "_onResize", self.resizeCharts.bind(self));
									self.own(self._resizeHandle);
								} else if (chartNode.scrollWidth > chartNode.offsetWidth) {
									// add CSS to allow wrapping of charts
									domClass.add(chartNode, "tableViewChartsWrap");
								}
							}
							dfd.resolve();
						});
					} else {
						if (view.get("chartHideTable")) {
							// if no charts and not showing table, then output message indicating nothing to display
							html.create("span", {
								"class": "csi-view-message",
								innerText: locale.resource(view.emptyMessageResource || "SEARCH_NO_RESULTS")
							}, chartNode);
						}
						dfd.resolve();
					}
				},
				function(err) {
					console.warn("ToolbarPluginChart#renderCharts - could not render charts");
					dfd.resolve();
				}
			);

			return dfd;
		}
	});
});